import moment from 'moment';
import React from 'react';

import App from '../Config/App';
import { LotteryTypes } from '../components/play/Types/Enums';

import { LotteryType } from 'Api/CRM/Responses/LotteryResponse';
import { LineNumbers, LotteryRanges } from 'components/play/Types/Types';

/**
 * assumes one number per pool
 *
 * @param ranges - lottery ranges
 * @param numbersToPick - amount of numbers to generate
 */
export function generateRandomLine(ranges: LotteryRanges, numbersToPick: number): LineNumbers {
    const random = [];

    for (let i = 0; i < numbersToPick; i++) {
        const start = ranges[i][0];
        const end = ranges[i][1];
        const value = Math.floor(Math.random() * (end + 1) + start);

        random.push([value]);
    }

    return random;
}

/**
 * Add leading zeros to number. e.g. from 648 to 000648
 */
export function padNumber(number: number | string, padLength: number) {
    return number.toString().padStart(padLength, '0');
}

export function formatNumber(number: number, lotteryType: LotteryType, padLength: number | null = null): string {
    if ([LotteryTypes.RaffleOpen, LotteryTypes.RaffleClosed].includes(lotteryType as LotteryTypes)) {
        return padNumber(number, padLength);
    }

    return number.toString();
}

export function isDoubleChanceLottery(slug: string): boolean {
    return ['nap-2-double-chance-cameroon'].includes(slug);
}

export function isDatePast(date: string): boolean {
    const now = new Date();
    const dateToCheck = new Date(date);

    return dateToCheck < now;
}

export function isAgentsPage(): boolean {
    return window.location.pathname.includes('agents');
}

export function triggerDeprecation(message: string) {
    if (!App.isProduction()) {
        console.trace(`Deprecation: ${message}`);
    }
}

/**
 * Add dusk tag for testing purposes
 * Usage: <div {...dusk('test-dusk-id')}></div> will produce HTML like that <div dusk="test"></div>
 *
 * @param {string} tag
 * @return {object} an empty object if env is invalid (local and testing are OK)
 */
export function dusk(tag: string): object {
    if (!App.isProduction()) {
        return { 'data-dusk': tag };
    }

    return {};
}

export function numberArray(length: number, inclusive = false): number[] {
    const adjustedLength = inclusive ? length + 1 : length;
    return adjustedLength >= 0 ? [...Array(adjustedLength).keys()] : [];
}

export function rangeArray(from: number, to: number): number[] {
    return to >= from ? [...Array(to - from + 1).keys()].map((i) => i + from) : [];
}

export function randomNumber(from: number, to: number): number {
    return Math.floor(Math.random() * (to - from + 1) + from);
}

export function getMobileOperatingSystem(): 'Android' | 'iOS' | 'unknown' {
    if (/android/i.test(navigator.userAgent)) return 'Android';
    if (/iPad|iPhone/.test(navigator.userAgent)) return 'iOS';
    return 'unknown';
}

export function updateInnerHTML(ref: React.RefObject<HTMLElement>, content: string): void {
    if (ref?.current) {
        ref.current.innerHTML = content;
    }
}

export function getVisibleProviderName(providerName: string): string {
    if (providerName === 'twitter') {
        return 'X';
    }
    if (providerName === 'partner') {
        return process.env.MIX_PARTNER_CLIENT_NAME !== '' ? process.env.MIX_PARTNER_CLIENT_NAME : providerName;
    }
    return providerName;
}

export function convertUtcToUserTimezone(utcDate: string | Date): string {
    const date = typeof utcDate === 'string' ? utcDate : utcDate.toISOString();
    return moment.tz(date, 'UTC').tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');
}

// Use for datetime-local input
export function convertToLocalDateTimeString(utcDate: string | Date): string {
    const date = typeof utcDate === 'string' ? utcDate : utcDate.toISOString();
    return moment.tz(date, 'UTC').tz(moment.tz.guess()).format('YYYY-MM-DDTHH:mm');
}

export function goBack() {
    window.history.back();
}

export function arrayWith<T>(array: T[], index: number, value: T): T[] {
    if (index < 0 || index >= array.length) {
        throw new RangeError('Index out of bounds');
    }
    return array.map((item, i) => (i === index ? value : item));
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value);
}
